import React, { useEffect, useState, useCallback } from 'react';
import config from '../config/config';
import { authFetch } from '../utils/api';
import EmptyState from './common/EmptyState';
import Skeleton from './common/Skeleton';
import { TaintFlowGraph } from './TaintFlowGraph';

const SEV_ORDER = ['critical', 'high', 'medium', 'low', 'info'];
const SEV_COLORS = {
  critical: '#dc2626',
  high: '#ea580c',
  medium: '#d97706',
  low: '#2563eb',
  info: '#64748b',
};
const SEV_BG = {
  critical: '#fee2e2',
  high: '#ffedd5',
  medium: '#fef3c7',
  low: '#dbeafe',
  info: '#f1f5f9',
};

function SevBadge({ sev }) {
  const cls = ['critical','high','medium','low','info'].includes(sev) ? sev : 'none';
  return <span className={`ce-sev ce-sev--${cls}`}>{sev}</span>;
}

function SevBar({ scan }) {
  const total = scan.totalFindings || 0;
  if (total === 0) return <span style={{ color: '#64748b', fontSize: 12 }}>No findings</span>;
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
      {SEV_ORDER.map((sev) => {
        const count = scan[sev] || 0;
        if (count === 0) return null;
        return (
          <span key={sev} style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 8px',
            borderRadius: 6,
            background: SEV_BG[sev],
            color: SEV_COLORS[sev],
            fontWeight: 700,
            fontSize: 12,
          }}>
            {count} {sev}
          </span>
        );
      })}
    </div>
  );
}

export default function RepoSastView({ repoName }) {

  const [scans, setScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeScan, setActiveScan] = useState(null);
  const [findings, setFindings] = useState([]);
  const [findingsLoading, setFindingsLoading] = useState(false);

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);

  const [sevFilter, setSevFilter] = useState('all');
  const [ruleFilter, setRuleFilter] = useState('');
  const [fileFilter, setFileFilter] = useState('');
  const [page, setPage] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);
  const PAGE_SIZE = 25;

  const fetchScans = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${config.BASE_URL}/repos/${encodeURIComponent(repoName)}/sast`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = data.scans || [];
      setScans(list);
      if (list.length > 0) {
        const latest = list[0];
        setActiveScan(latest);
        // Fetch full scan (with findings blob) separately
        fetchFullScan(latest.id);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoName]);

  async function fetchFullScan(scanId) {
    setFindingsLoading(true);
    try {
      const res = await authFetch(`${config.BASE_URL}/vigil/scan/${scanId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const scan = await res.json();
      setActiveScan(scan);
      loadFindings(scan);
    } catch (e) {
      setFindings([]);
    } finally {
      setFindingsLoading(false);
    }
  }

  function loadFindings(scan) {
    setFindingsLoading(true);
    try {
      // When loaded via GET /vigil/scan/:id, findings is already an array
      // When loaded from the list response, findings is a JSON string (or empty)
      let raw = scan.findings;
      if (typeof raw === 'string') {
        raw = raw ? JSON.parse(raw) : [];
      }
      setFindings(Array.isArray(raw) ? raw : []);
    } catch {
      setFindings([]);
    } finally {
      setFindingsLoading(false);
    }
    setPage(0);
  }

  useEffect(() => { fetchScans(); }, [fetchScans]);

  async function runScan() {
    setRunning(true);
    setRunError(null);
    try {
      const res = await authFetch(`${config.BASE_URL}/repos/${encodeURIComponent(repoName)}/sast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      const scanId = data.id || data.scanId;
      if (scanId) {
        pollScan(scanId);
      }
    } catch (e) {
      setRunError(e.message);
      setRunning(false);
    }
  }

  async function pollScan(scanId) {
    let attempts = 0;
    const maxAttempts = 180; // 3 min @ 1s intervals
    const interval = setInterval(async () => {
      attempts++;
      try {
        const res = await authFetch(`${config.BASE_URL}/vigil/scan/${scanId}`);
        if (!res.ok) return;
        const scan = await res.json();
        if (scan.status === 'done' || scan.status === 'failed') {
          clearInterval(interval);
          setRunning(false);
          fetchScans();
        }
      } catch { /* ignore polling errors */ }
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        setRunning(false);
      }
    }, 1000);
  }

  // Filter findings
  const filtered = findings.filter(f => {
    if (sevFilter !== 'all' && (f.severity || '').toLowerCase() !== sevFilter) return false;
    if (ruleFilter && !(f.ruleId || '').toLowerCase().includes(ruleFilter.toLowerCase())) return false;
    if (fileFilter && !(f.file || '').toLowerCase().includes(fileFilter.toLowerCase())) return false;
    return true;
  });
  const paged = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  if (loading) return <div style={{ padding: 24 }}><Skeleton lines={6} /></div>;
  if (error) return <div className="alert alert-danger m-3">{error}</div>;

  return (
    <div style={{ padding: '20px 24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h5 style={{ margin: 0, fontWeight: 700 }}>🛡️ SAST — Vigil Security Scan</h5>
          <div style={{ color: '#64748b', fontSize: 12, marginTop: 3 }}>
            Static application security testing powered by Vigil
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className={`ce-btn ce-btn--sm ${running ? 'ce-btn--secondary' : 'ce-btn--primary'}`}
            onClick={runScan}
            disabled={running}
          >
            {running ? '⏳ Scanning…' : scans.length === 0 ? '▶ Run First Scan' : '🔄 Re-scan'}
          </button>
        </div>
      </div>

      {runError && <div className="alert alert-danger py-2 px-3 mb-3" style={{ fontSize: 13 }}>{runError}</div>}

      {/* Running banner */}
      {running && (
        <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: 13 }}>
          ⏳ Vigil scan running… Results will appear automatically when complete.
        </div>
      )}

      {/* No scans yet */}
      {scans.length === 0 && !running && (
        <EmptyState
          icon="🛡️"
          title="No SAST scans yet"
          description="Run a Vigil security scan to detect vulnerabilities in this repository's source code."
        />
      )}

      {/* Scan summary cards + history */}
      {scans.length > 0 && (
        <>
          {/* Latest scan summary */}
          {activeScan && (
            <div className="ce-kpi-row" style={{ marginBottom: 20 }}>
              {[
                { label: 'Status', value: activeScan.status, color: activeScan.status === 'done' ? '#16a34a' : activeScan.status === 'failed' ? '#dc2626' : '#d97706' },
                { label: 'Total Findings', value: activeScan.totalFindings ?? '—', color: '#6366f1' },
                { label: 'Critical', value: activeScan.critical ?? 0, color: activeScan.critical > 0 ? '#dc2626' : '#6b7280' },
                { label: 'High', value: activeScan.high ?? 0, color: activeScan.high > 0 ? '#ea580c' : '#6b7280' },
                { label: 'Medium', value: activeScan.medium ?? 0, color: '#ca8a04' },
                { label: 'Rules Run', value: activeScan.totalRules ?? '—', color: '#6366f1' },
                { label: 'Duration', value: activeScan.duration || '—', color: '#6366f1' },
              ].map(card => (
                <div key={card.label} className="ce-kpi" style={{ '--kpi-color': card.color }}>
                  <div className="ce-kpi-value">{card.value}</div>
                  <div className="ce-kpi-label">{card.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Scan history selector */}
          {scans.length > 1 && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginRight: 8 }}>Scan history:</label>
              <select
                className="ce-filter-select"
                style={{ width: 'auto', fontSize: 12 }}
                value={activeScan?.id || ''}
                onChange={e => {
                  const s = scans.find(sc => sc.id === e.target.value);
                  if (s) { setActiveScan(s); fetchFullScan(s.id); }
                }}
              >
                {scans.map((s, i) => (
                  <option key={s.id} value={s.id}>
                    {i === 0 ? '(latest) ' : ''}{new Date(s.createdAt).toLocaleString()} — {s.totalFindings} findings
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Findings */}
          {activeScan?.status === 'done' && (
            <>
              {/* Severity distribution */}
              {activeScan.totalFindings > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <SevBar scan={activeScan} />
                </div>
              )}

              {/* Filters */}
              <div className="ce-filter-bar" style={{ marginBottom: 14 }}>
                <select
                  className="ce-filter-select"
                  style={{ width: 130 }}
                  value={sevFilter}
                  onChange={e => { setSevFilter(e.target.value); setPage(0); }}
                >
                  <option value="all">All severities</option>
                  {SEV_ORDER.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <input
                  className="ce-search-input"
                  style={{ width: 180 }}
                  placeholder="Filter by rule…"
                  value={ruleFilter}
                  onChange={e => { setRuleFilter(e.target.value); setPage(0); }}
                />
                <input
                  className="ce-search-input"
                  style={{ width: 200 }}
                  placeholder="Filter by file…"
                  value={fileFilter}
                  onChange={e => { setFileFilter(e.target.value); setPage(0); }}
                />
                {(sevFilter !== 'all' || ruleFilter || fileFilter) && (
                  <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={() => { setSevFilter('all'); setRuleFilter(''); setFileFilter(''); setPage(0); }}>
                    Clear
                  </button>
                )}
                <span className="ce-filter-count" style={{ marginLeft: 'auto' }}>
                  {filtered.length} finding{filtered.length !== 1 ? 's' : ''}
                </span>
              </div>

              {findingsLoading ? (
                <Skeleton lines={5} />
              ) : filtered.length === 0 ? (
                <EmptyState icon="✅" title="No findings" description="No SAST findings match the current filters." />
              ) : (
                <>
                  <div className="ce-table-wrap">
                    <table className="ce-table ce-table--clickable">
                      <thead>
                        <tr>
                          <th>Severity</th>
                          <th>Rule</th>
                          <th>File</th>
                          <th>Line</th>
                          <th>Message</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paged.map((f, i) => {
                          const sev = (f.severity || 'info').toLowerCase();
                          const file = f.file || '—';
                          const line = f.startLine || '—';
                          const rowId = f.id || `${f.ruleId}::${f.file}::${f.startLine}::${i}`;
                          const isExpanded = expandedRow === rowId;
                          const shortFile = (p) => {
                            if (!p) return '—';
                            const parts = p.replace(/\\/g, '/').split('/');
                            return parts.length > 3 ? '…/' + parts.slice(-2).join('/') : p;
                          };
                          return (
                            <React.Fragment key={rowId}>
                              <tr
                                onClick={() => setExpandedRow(isExpanded ? null : rowId)}
                                style={{
                                  borderBottom: isExpanded ? 'none' : '1px solid #f1f5f9',
                                  cursor: 'pointer',
                                  background: isExpanded ? '#f0f4ff' : 'transparent',
                                  transition: 'background 0.15s',
                                }}
                              >
                                <td style={{ padding: '8px 12px' }}><SevBadge sev={sev} /></td>
                                <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11, color: '#1e293b', maxWidth: 160, wordBreak: 'break-all' }}>
                                  {f.ruleId || '—'}
                                </td>
                                <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 11, color: '#475569', maxWidth: 220, wordBreak: 'break-all' }} title={file}>
                                  {shortFile(file)}
                                </td>
                                <td style={{ padding: '8px 12px', color: '#64748b' }}>{line}</td>
                                <td style={{ padding: '8px 12px', color: '#334155', maxWidth: 320 }}>
                                  <span style={{ marginRight: 6 }}>{f.message || '—'}</span>
                                  <span style={{ fontSize: 10, color: '#94a3b8' }}>{isExpanded ? '▲' : '▼'}</span>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                  <td colSpan={5} style={{ padding: 0, background: '#f8fafc', borderTop: '2px solid #e0e7ff' }}>
                                    <div style={{ padding: '16px 20px' }}>
                                      {/* TaintFlowGraph */}
                                      {f.flowPath && f.flowPath.length > 0
                                        ? <TaintFlowGraph flowPath={f.flowPath} ruleId={f.ruleId} severity={f.severity} />
                                        : (
                                          <div style={{ color: '#92400e', fontSize: 12, fontStyle: 'italic', marginBottom: 12, padding: '10px 14px', background: '#fffbeb', border: '1px solid #fef3c7', borderRadius: 8 }}>
                                            ⚠️ No flow path recorded — <strong>re-run the scan</strong> to see the TaintFlowGraph with step-by-step code analysis.
                                          </div>
                                        )
                                      }
                                      {/* Metadata grid */}
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 12 }}>
                                        {[
                                          { label: 'Rule', value: f.ruleId, mono: true },
                                          { label: 'Severity', value: (f.severity || '—').toUpperCase(), color: SEV_COLORS[sev] },
                                          { label: 'Confidence', value: f.confidence || '—' },
                                          { label: 'Engine', value: f.engine || '—' },
                                          { label: 'File', value: file, mono: true, title: file },
                                          { label: 'Line', value: `${f.startLine || '—'}:${f.startColumn || '—'}` },
                                        ].map(m => (
                                          <div key={m.label} style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 14px', minWidth: 100, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                                            <div style={{ fontSize: 10, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.07em', marginBottom: 3, fontWeight: 600 }}>{m.label}</div>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: m.color || '#1e293b', fontFamily: m.mono ? 'monospace' : undefined, wordBreak: 'break-all' }} title={m.title}>{m.value}</div>
                                          </div>
                                        ))}
                                      </div>
                                      {/* Message */}
                                      <div style={{ marginTop: 14, fontSize: 12.5, color: '#334155', lineHeight: 1.6, borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
                                        <span style={{ color: '#94a3b8', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '.07em', marginRight: 8 }}>Message</span>
                                        {f.message}
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
                      <button className="ce-btn ce-btn--secondary ce-btn--sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>← Prev</button>
                      <span style={{ fontSize: 12, alignSelf: 'center', color: '#64748b' }}>
                        Page {page + 1} / {totalPages}
                      </span>
                      <button className="ce-btn ce-btn--secondary ce-btn--sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>Next →</button>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {activeScan?.status === 'failed' && (
            <div className="alert alert-danger py-2 px-3" style={{ fontSize: 13 }}>
              ❌ Scan failed: {activeScan.error || 'Unknown error'}
            </div>
          )}

          {activeScan && !['done', 'failed'].includes(activeScan.status) && (
            <div className="alert alert-info py-2 px-3" style={{ fontSize: 13 }}>
              ⏳ Scan status: <strong>{activeScan.status}</strong>
            </div>
          )}
        </>
      )}
    </div>
  );
}
